import { Columns2, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '@/api';
import type { WorkItem } from '@/api/areas/work';
import { Callout, Section } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { useApi } from '@/lib/useApi';
import { FileFrame, ReadFileDialog } from './shared';
import { UploadDetail } from './SubjectDeclarations';

/**
 * A `DECLARATION_REVIEW` work item: the check table of the upload, and the generated PDF next to
 * the uploaded file (read with a reason, audited; needs identity_document.view).
 */
export function DeclarationReviewPanel({ item }: { item: WorkItem }) {
  const { t } = useTranslation('declarations');
  const declarationId = typeof item.detail.declaration_id === 'string' ? item.detail.declaration_id : '';
  const submissionId = typeof item.detail.submission_id === 'string' ? item.detail.submission_id : '';
  const scan = item.detail.upload_kind === 'SCAN';
  const matches = item.detail.everything_matches === true;
  const result = useApi((signal) => api.getDeclaration(declarationId, { signal }), [declarationId]);
  const [asking, setAsking] = useState(false);
  const [files, setFiles] = useState<{ generated: Blob; uploaded: Blob } | null>(null);
  if (!declarationId) return null;
  const upload = result.status === 'success' ? result.data.uploads.find((u) => u.submission_id === submissionId) : undefined;
  return (
    <Section
      title={t('review.title')}
      description={scan ? t('review.scanNote') : t('review.signedNote')}
      actions={
        <>
          <Button size="sm" variant="outline" onClick={() => setAsking(true)}>
            <Columns2 /> {t('review.show')}
          </Button>
          {item.subject_id ? (
            <Button size="sm" variant="ghost" asChild>
              <Link to={`/subjects/${encodeURIComponent(item.subject_id)}?tab=declarations`}>
                <ExternalLink /> {t('review.openSubject')}
              </Link>
            </Button>
          ) : null}
        </>
      }
    >
      <div className="grid gap-4">
        <Callout tone={matches ? 'ok' : 'warn'}>{matches ? t('review.matches') : t('review.notEverything')}</Callout>
        <p className="text-xs text-muted-foreground">{t('review.approveEffect')}</p>
        {result.status === 'loading' ? <Loading rows={3} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
        {upload ? <UploadDetail u={upload} /> : null}
        {files ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="grid gap-2">
              <p className="text-sm font-medium">{t('review.generated')}</p>
              <FileFrame blob={files.generated} title={t('review.generated')} />
            </div>
            <div className="grid gap-2">
              <p className="text-sm font-medium">{t('review.uploaded')}</p>
              <FileFrame blob={files.uploaded} title={t('review.uploaded')} />
            </div>
          </div>
        ) : null}
      </div>
      <ReadFileDialog
        open={asking}
        onOpenChange={setAsking}
        load={async (reason) => {
          const [generated, uploaded] = await Promise.all([api.declarationPdf(declarationId, reason), api.declarationUploadFile(submissionId, reason)]);
          setFiles({ generated, uploaded });
          return uploaded;
        }}
        onLoaded={() => undefined}
      />
    </Section>
  );
}
